import { useState } from 'react'
import type { Room } from '../types'
import { Button } from '../components/Button'
import { Modal } from '../components/Modal'
import styles from './RoomCard.module.css'

interface RoomCardProps {
  room: Room
  onEdit: (room: Room) => void
  onDelete: (room: Room) => void
}

const ROOM_TYPE_LABELS: Record<string, string> = {
  dormitorio: 'Dormitorio',
  cocina: 'Cocina',
  bano: 'Baño',
  baño: 'Baño',
  living: 'Living',
  lavanderia: 'Lavandería',
  lavandería: 'Lavandería',
  garage: 'Garage',
  exterior: 'Exterior',
  otro: 'Otro',
}

const SPECIAL_LABELS: Record<string, string> = {
  lavadora: 'Lavadora',
  secadora: 'Secadora',
  horno_electrico: 'Horno eléctrico',
  aire_acondicionado: 'Aire acond.',
  cargador_auto: 'Auto eléctrico',
}

function pluralizeLuces(n: number): string {
  return n === 1 ? '1 luz' : `${n} luces`
}

function pluralizeEnchufes(n: number): string {
  return n === 1 ? '1 enchufe' : `${n} enchufes`
}

export function RoomCard({ room, onEdit, onDelete }: RoomCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const widthM = (room.width_cm / 100).toFixed(1)
  const heightM = (room.height_cm / 100).toFixed(1)
  const typeLabel = ROOM_TYPE_LABELS[room.type?.toLowerCase()] ?? room.type
  const specialCircuits = Array.isArray(room.special_circuits)
    ? (room.special_circuits as Array<string | { type: string }>).map((c) =>
        typeof c === 'string' ? c : c.type
      )
    : []

  const handleConfirmDelete = async () => {
    setDeleting(true)
    onDelete(room)
    setConfirmDelete(false)
    setDeleting(false)
  }

  return (
    <>
      <div className={styles.card}>
        <div className={styles.top}>
          <h3 className={styles.name}>{room.name}</h3>
          <span className={styles.typeBadge}>{typeLabel}</span>
        </div>
        <div className={styles.info}>
          <span className={styles.infoItem}>{widthM}m × {heightM}m</span>
          <span className={styles.infoItem}>{pluralizeLuces(room.light_points)}</span>
          <span className={styles.infoItem}>{pluralizeEnchufes(room.outlet_points)}</span>
          {specialCircuits.map((key) => (
            <span key={key} className={styles.specialBadge}>
              {SPECIAL_LABELS[key] ?? key}
            </span>
          ))}
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => onEdit(room)}>
            Editar
          </Button>
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            Eliminar
          </Button>
        </div>
      </div>

      <Modal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Eliminar recinto"
      >
        <p className={styles.confirmText}>
          ¿Eliminar el recinto <strong>{room.name}</strong>? Esta acción no se puede deshacer.
        </p>
        <div className={styles.confirmActions}>
          <Button
            variant="danger"
            loading={deleting}
            onClick={handleConfirmDelete}
            fullWidth
          >
            Sí, eliminar
          </Button>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)} fullWidth>
            Cancelar
          </Button>
        </div>
      </Modal>
    </>
  )
}
